import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../../../design-system';
import { getSuggestedReorder, SuggestedReorderLine } from '../../../../services/ordersApi';
import styles from './SuggestedReorderModal.module.css';

interface SuggestedReorderModalProps {
  isOpen: boolean;
  customerId: number;
  customerName: string;
  onClose: () => void;
}

/**
 * VP-SALES-004 (Phase 1 scope): shows the backend's avg-qty-per-SKU
 * suggestion for a customer, lets the rep edit each quantity, then hands the
 * edited line items to the New Order wizard via router state — the wizard
 * pre-fills its customer + cart from this instead of the rep re-picking
 * every SKU/quantity from scratch.
 */
export const SuggestedReorderModal: React.FC<SuggestedReorderModalProps> = ({
  isOpen,
  customerId,
  customerName,
  onClose,
}) => {
  const navigate = useNavigate();
  const [lines, setLines] = useState<SuggestedReorderLine[]>([]);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSuggestedReorder(customerId)
      .then((data) => {
        if (cancelled) return;
        setLines(data);
        setQuantities(Object.fromEntries(data.map((l) => [l.sku_id, l.suggested_quantity])));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load suggestions');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, customerId]);

  const handleQtyChange = (skuId: number, value: string) => {
    const n = parseInt(value, 10);
    setQuantities((prev) => ({ ...prev, [skuId]: Number.isFinite(n) && n > 0 ? n : 0 }));
  };

  const handleCreateOrder = () => {
    const prefillItems = lines
      .map((l) => ({ sku_id: l.sku_id, quantity: quantities[l.sku_id] ?? l.suggested_quantity }))
      .filter((l) => l.quantity > 0);
    navigate('/new-order', { state: { prefillCustomerId: String(customerId), prefillItems } });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header>
        <h2 className={styles.title}>Suggested Reorder — {customerName}</h2>
      </Modal.Header>

      <Modal.Body>
        {loading && <p className={styles.status}>Loading suggestions…</p>}
        {error && <p className={styles.error}>{error}</p>}
        {!loading && !error && lines.length === 0 && (
          <p className={styles.status}>No recent order history to base a suggestion on yet.</p>
        )}
        {!loading && !error && lines.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th>Based on</th>
                <th>Suggested qty</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.sku_id}>
                  <td>{line.sku_code}</td>
                  <td>{line.product_name}</td>
                  <td>{line.based_on_orders} order{line.based_on_orders === 1 ? '' : 's'}</td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      className={styles.qtyInput}
                      value={quantities[line.sku_id] ?? line.suggested_quantity}
                      onChange={(e) => handleQtyChange(line.sku_id, e.target.value)}
                      aria-label={`Quantity for ${line.product_name}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className={styles.footerActions}>
          <button className={styles.secondaryBtn} onClick={onClose} type="button">Cancel</button>
          <button
            className={styles.primaryBtn}
            onClick={handleCreateOrder}
            type="button"
            disabled={loading || lines.length === 0}
          >
            Create Order with these quantities
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SuggestedReorderModal;
